import { z } from 'zod';
import { bloodTypeSchema, genderSchema } from './enums.js';

/** Patient contracts (docs/spec.md §4.2, §7 screen 4). */

const phone = z
  .string()
  .trim()
  .max(32)
  .regex(/^[\d\s+()-]*$/, 'phone_format')
  .optional()
  .or(z.literal(''));

export const patientInputSchema = z
  .object({
    fullName: z.string().trim().min(1).max(160),
    // Empty is allowed — the registration form's "—" option, same as bloodType below.
    gender: genderSchema.optional().or(z.literal('')),
    /** ISO date. Optional because many patients only know their age. */
    dob: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'date_format')
      .optional()
      .or(z.literal('')),
    ageYears: z.coerce.number().int().min(0).max(130).optional(),
    phone,
    occupation: z.string().trim().max(120).optional().or(z.literal('')),
    address: z.string().trim().max(255).optional().or(z.literal('')),
    // Empty is allowed — plenty of patients simply have not been typed.
    bloodType: bloodTypeSchema.optional().or(z.literal('')),
    allergies: z.string().trim().max(2000).optional().or(z.literal('')),
    note: z.string().trim().max(2000).optional().or(z.literal('')),
    /**
     * Patient number from whatever the clinic used before — it appears as
     * "Ext HN" on their lab reports. Optional free text: we do not yet know
     * its format or origin (docs/spec.md §13 Q3).
     */
    extHn: z.string().trim().max(32).optional().or(z.literal('')),
    /** Ward/department from the referring hospital — printed as "Ward" on the lab report. */
    ward: z.string().trim().max(64).optional().or(z.literal('')),
  })
  .refine((value) => Boolean(value.dob) || value.ageYears !== undefined, {
    // One of the two must be present, or the lab report header and any
    // age-dependent reference range would have nothing to print.
    message: 'dob_or_age_required',
    path: ['ageYears'],
  });

export type PatientInput = z.infer<typeof patientInputSchema>;

/** Partial edit; the dob/age rule is enforced against the merged row server-side. */
export const patientUpdateSchema = patientInputSchema.innerType().partial();
export type PatientUpdate = z.infer<typeof patientUpdateSchema>;

export interface Patient {
  id: number;
  code: string;
  extHn: string | null;
  ward: string | null;
  fullName: string;
  gender: 'female' | 'male' | 'other' | null;
  dob: string | null;
  ageYears: number | null;
  phone: string | null;
  occupation: string | null;
  address: string | null;
  bloodType: string | null;
  allergies: string | null;
  note: string | null;
  createdAt: string;
}

/** Row shape for the patients table — deliberately narrower than `Patient`. */
export interface PatientListItem {
  id: number;
  code: string;
  fullName: string;
  gender: 'female' | 'male' | 'other' | null;
  ageYears: number | null;
  phone: string | null;
  lastVisitAt: string | null;
}

/**
 * Age as it should be displayed: computed from the birth date when we have one,
 * otherwise the stored figure — which then ages with the record, so the date is
 * always preferred.
 */
export function displayAge(patient: {
  dob?: string | null;
  ageYears?: number | null;
}): number | null {
  if (patient.dob) {
    const born = new Date(patient.dob);
    if (!Number.isNaN(born.getTime())) {
      const now = new Date();
      let age = now.getFullYear() - born.getFullYear();
      const monthDiff = now.getMonth() - born.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < born.getDate())) age -= 1;
      return age;
    }
  }
  return patient.ageYears ?? null;
}
